import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  ErrorPanel,
  Loading,
  PageHeader,
} from "../components/ResourceView";
import { useOrganization } from "../org/OrganizationProvider";
import { useRpcResource } from "../lib/useRpcResource";
import {
  fieldCrewApi,
  type FieldCrewAssignment,
  type OperationsDivision,
} from "../lib/fieldCrew";
const isoDate = (d: Date) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};
const label = (v: string | null | undefined) =>
  v ? String(v).replaceAll("_", " ") : "—";
const dt = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleString() : "—";
export function FieldCrewBoardPage({
  division = "local_event",
}: {
  division?: OperationsDivision;
}) {
  const { organizationId } = useOrganization();
  const national = division === "national";
  const [startDate, setStartDate] = useState(isoDate(new Date()));
  const [endDate, setEndDate] = useState(
    isoDate(new Date(Date.now() + 14 * 86400000)),
  );
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarToken, setCalendarToken] = useState<string | null>(null);
  const [calendarBusy, setCalendarBusy] = useState(false);
  const [calendarMessage, setCalendarMessage] = useState<string | null>(null);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const r = useRpcResource(
    () =>
      organizationId
        ? fieldCrewApi.workspace(organizationId, startDate, endDate)
        : Promise.resolve({
            data: null,
            error: { message: "Organization context is missing." },
          }),
    [organizationId, startDate, endDate],
  );
  const sub = useRpcResource(
    () =>
      !national && organizationId
        ? fieldCrewApi.calendarStatus(organizationId)
        : Promise.resolve({ data: null, error: null }),
    [organizationId, national],
  );
  const assignments = (r.data?.assignments ?? []).filter(
    (a) => a.operations_division === division,
  );
  const jobs = useMemo(() => groupJobs(assignments), [assignments]);
  const days = useMemo(() => {
    const out: Date[] = [];
    const start = new Date(`${startDate}T00:00:00`),
      end = new Date(`${endDate}T00:00:00`);
    for (
      let d = new Date(start);
      d <= end && out.length < 31;
      d.setDate(d.getDate() + 1)
    )
      out.push(new Date(d));
    return out;
  }, [startDate, endDate]);
  const feedUrl = calendarToken
    ? fieldCrewApi.calendarFeedUrl(calendarToken)
    : null;
  const jobHref = (id: string) =>
    national ? `/national/field/jobs/${id}` : `/field/jobs/${id}`;
  async function createCalendar() {
    if (!organizationId || national) return;
    setCalendarBusy(true);
    setCalendarMessage(null);
    setCalendarError(null);
    const x = await fieldCrewApi.createCalendarSubscription(organizationId);
    if (x.error) setCalendarError(x.error.message);
    else if (x.data) {
      setCalendarToken(x.data.token);
      setCalendarMessage(
        "Subscription link created. This link updates automatically when Dispatch changes assigned work.",
      );
      await sub.reload();
    }
    setCalendarBusy(false);
  }
  async function revokeCalendar() {
    if (!organizationId || national) return;
    setCalendarBusy(true);
    setCalendarMessage(null);
    setCalendarError(null);
    const x = await fieldCrewApi.revokeCalendarSubscription(organizationId);
    if (x.error) setCalendarError(x.error.message);
    else {
      setCalendarToken(null);
      setCalendarMessage("Field calendar subscription revoked.");
      await sub.reload();
    }
    setCalendarBusy(false);
  }
  async function copyCalendar() {
    if (!feedUrl) return;
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCalendarMessage("Subscription URL copied.");
      setCalendarError(null);
    } catch {
      setCalendarError(
        "Could not copy automatically. Select the URL and copy it manually.",
      );
    }
  }
  return (
    <>
      <PageHeader
        title={
          national ? "National Field Operations" : "Local Field Operations"
        }
        subtitle={
          national
            ? "National employee and crew assignments routed from National Operations & Dispatch."
            : "Local event crew schedule and Dispatch-assigned field tasks."
        }
        actions={
          <>
            {!national && (
              <button onClick={() => setShowCalendar((v) => !v)}>
                Subscribe Calendar
              </button>
            )}
            <Link
              className="button-link"
              to={national ? "/national/jobs" : "/operations/jobs"}
            >
              {national ? "National Jobs & Dispatch" : "Local Jobs & Dispatch"}
            </Link>
            <Link className="button-link" to="/time/timecards">
              Crew Timecards
            </Link>
            <button onClick={() => void r.reload()}>Refresh</button>
          </>
        }
      />
      {national && (
        <div className="notice notice-info">
          National field crews are kept separate from Local Event crews. Open an
          assigned Job to use the same Field Task Checklist, complete BOM and
          shared sign-off attachments used by Local Field Operations.
        </div>
      )}
      {!national && showCalendar && (
        <Card title="Subscribe to Local Field Jobs calendar">
          <div className="field-calendar-subscribe">
            <div>
              <strong>
                {sub.data?.active
                  ? "Calendar subscription active"
                  : "No active subscription"}
              </strong>
              <p className="muted">
                Add this live local schedule to Outlook, Apple Calendar, Google
                Calendar or another calendar app. It stays synchronized with
                Dispatch assignments. Creating a new link disables the previous
                link.
              </p>
              {sub.data?.active && (
                <small>
                  Created {dt(sub.data.created_at)}
                  {sub.data.last_accessed_at
                    ? ` · Last calendar sync ${dt(sub.data.last_accessed_at)}`
                    : ""}
                </small>
              )}
            </div>
            {calendarMessage && (
              <div className="notice notice-success">{calendarMessage}</div>
            )}
            {calendarError && <ErrorPanel message={calendarError} />}
            <div className="field-calendar-actions">
              <button
                className="primary"
                disabled={calendarBusy}
                onClick={() => void createCalendar()}
              >
                {sub.data?.active
                  ? "Create replacement link"
                  : "Create subscription link"}
              </button>
              {sub.data?.active && (
                <button
                  disabled={calendarBusy}
                  onClick={() => void revokeCalendar()}
                >
                  Revoke subscription
                </button>
              )}
            </div>
            {feedUrl && (
              <div className="field-calendar-link">
                <label className="field">
                  <span>Private Field calendar URL</span>
                  <input readOnly value={feedUrl} />
                </label>
                <div className="row-actions">
                  <a
                    className="button-link primary"
                    href={feedUrl.replace(/^https:/, "webcal:")}
                  >
                    Subscribe in calendar app
                  </a>
                  <button onClick={() => void copyCalendar()}>Copy URL</button>
                </div>
                <small>
                  Apple Calendar / Outlook: use “Subscribe in calendar app.”
                  Google Calendar: copy the URL, then use Add calendar → From
                  URL. Treat this URL like a password.
                </small>
              </div>
            )}
          </div>
        </Card>
      )}
      <Card>
        <div className="filter-bar">
          <label className="field">
            <span>From</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Through</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
        </div>
      </Card>
      {r.loading ? (
        <Loading />
      ) : r.error ? (
        <ErrorPanel message={r.error} />
      ) : (
        <>
          <div className="metric-grid three">
            <Metric label="Assigned jobs" value={jobs.length} />
            <Metric label="Assigned field tasks" value={assignments.length} />
            <Metric
              label="Active now"
              value={
                assignments.filter((a) =>
                  ["arrived", "in_progress", "qc_pending"].includes(
                    a.field_session_status ?? "",
                  ),
                ).length
              }
            />
          </div>
          <Card title="Crew schedule">
            <div className="field-job-calendar">
              {days.map((day) => {
                const key = isoDate(day);
                const items = assignments.filter(
                  (a) => isoDate(new Date(a.starts_at)) === key,
                );
                return (
                  <section
                    className={`field-job-day${key === isoDate(new Date()) ? " today" : ""}`}
                    key={key}
                  >
                    <header>
                      <strong>
                        {day.toLocaleDateString([], { weekday: "short" })}
                      </strong>
                      <span>
                        {day.toLocaleDateString([], {
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    </header>
                    {items.length === 0 ? (
                      <small>No assigned work</small>
                    ) : (
                      items.map((a) => (
                        <Link
                          to={jobHref(a.job_id)}
                          className="field-job-calendar-event"
                          key={a.assignment_id}
                        >
                          <strong>{a.job_name}</strong>
                          <span>
                            {label(a.phase_type)} · {a.crew_name}
                          </span>
                          <small>
                            {new Date(a.starts_at).toLocaleTimeString([], {
                              hour: "numeric",
                              minute: "2-digit",
                            })}{" "}
                            –{" "}
                            {new Date(a.ends_at).toLocaleTimeString([], {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </small>
                        </Link>
                      ))
                    )}
                  </section>
                );
              })}
            </div>
          </Card>
          <Card title="Assigned jobs">
            {jobs.length === 0 ? (
              <div className="empty-state">
                <strong>
                  No {national ? "National" : "Local Event"} jobs assigned in
                  this date range.
                </strong>
                <span>
                  Jobs appear here automatically when the matching Dispatch
                  workspace assigns a crew to a Job phase.
                </span>
              </div>
            ) : (
              <div className="field-assigned-job-list">
                {jobs.map((j) => (
                  <Link
                    to={jobHref(j.job_id)}
                    className="field-assigned-job"
                    key={j.job_id}
                  >
                    <div>
                      <span className="eyebrow">
                        {j.customer_name ?? "Customer"} · {j.job_number}
                      </span>
                      <h3>{j.job_name}</h3>
                      <p>
                        {[
                          j.site_name,
                          j.site_address_line1,
                          j.site_city,
                          j.site_state_region,
                        ]
                          .filter(Boolean)
                          .join(" · ") || "Site details pending"}
                      </p>
                    </div>
                    <div className="field-assigned-job-side">
                      <strong>
                        {j.assignments.length} assigned task
                        {j.assignments.length === 1 ? "" : "s"}
                      </strong>
                      <span>
                        {j.assignments
                          .map((x) => label(x.phase_type))
                          .join(" · ")}
                      </span>
                      <small>{dt(j.first_start)}</small>
                      <span>Open checklist →</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </>
  );
}
function groupJobs(assignments: FieldCrewAssignment[]) {
  const map = new Map<
    string,
    {
      job_id: string;
      job_number: string;
      job_name: string;
      customer_name: string | null;
      site_name: string | null;
      site_address_line1: string | null;
      site_city: string | null;
      site_state_region: string | null;
      first_start: string;
      assignments: FieldCrewAssignment[];
    }
  >();
  for (const a of assignments) {
    const current = map.get(a.job_id);
    if (current) {
      current.assignments.push(a);
      if (new Date(a.starts_at) < new Date(current.first_start))
        current.first_start = a.starts_at;
    } else
      map.set(a.job_id, {
        job_id: a.job_id,
        job_number: a.job_number,
        job_name: a.job_name,
        customer_name: a.customer_name,
        site_name: a.site_name,
        site_address_line1: a.site_address_line1,
        site_city: a.site_city,
        site_state_region: a.site_state_region,
        first_start: a.starts_at,
        assignments: [a],
      });
  }
  return [...map.values()].sort(
    (a, b) =>
      new Date(a.first_start).getTime() - new Date(b.first_start).getTime(),
  );
}
function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
